import { useState, type ChangeEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Input, useDialog } from '@gokrew/ui';
import { Upload } from 'lucide-react';
import { importCrmLeads, type CrmLeadRow } from '@/api/crm';
import { csvToLeads, parseCsv } from './crm-csv';
import { useCrmFormat } from './crm-format';
import { CrmButton } from './CrmShared';
import styles from './Crm.module.css';

/** The API takes 1,000 rows per call; smaller chunks show progress sooner. */
const CHUNK = 500;

/**
 * CSV import of leads (people and their companies). Columns are recognised
 * by their header (EN or FR, Apollo / LinkedIn exports); the file is sent in
 * chunks under one batch, and duplicates are skipped by the server.
 */
export const CrmLeadImport = ({ dialogId }: { dialogId?: string }) => {
  const { t } = useCrmFormat();
  const { closeDialog } = useDialog();
  const client = useQueryClient();
  const [file, setFile] = useState('');
  const [source, setSource] = useState('');
  const [leads, setLeads] = useState<CrmLeadRow[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [result, setResult] = useState<{
    inserted: number;
    skipped: number;
  } | null>(null);
  const read = async (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    setError('');
    setResult(null);
    setLeads([]);
    if (!picked) return;
    setFile(picked.name);
    setSource((old) => old || picked.name.replace(/\.[^.]+$/, ''));
    const { mapping, leads: rows } = csvToLeads(parseCsv(await picked.text()));
    const fields = Object.keys(mapping);
    if (
      !['email', 'companyName', 'firstName', 'lastName'].some((key) =>
        fields.includes(key)
      )
    ) {
      setError(t('crm.leads.import_no_columns'));
      return;
    }
    setColumns(fields);
    setLeads(rows);
  };
  const run = async () => {
    setError('');
    setProgress(0);
    let batchId: string | undefined;
    let inserted = 0;
    let skipped = 0;
    try {
      for (let start = 0; start < leads.length; start += CHUNK) {
        const done = await importCrmLeads({
          rows: leads.slice(start, start + CHUNK),
          source: source.trim() || undefined,
          batchId,
        });
        batchId = done.batchId;
        inserted += done.inserted;
        skipped += done.skipped;
        setProgress(Math.min(leads.length, start + CHUNK));
      }
      setResult({ inserted, skipped });
      await client.invalidateQueries({ queryKey: ['crm', 'leads'] });
    } catch {
      setError(t('crm.leads.import_error', { count: inserted }));
    } finally {
      setProgress(null);
    }
  };
  return (
    <div className={styles.form}>
      <p className={styles.muted}>{t('crm.leads.import_help')}</p>
      <label className={styles.fileDrop}>
        <Upload aria-hidden="true" />
        <span>{file || t('crm.leads.import_pick')}</span>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(event) => void read(event)}
        />
      </label>
      <label>
        {t('crm.leads.source')}
        <Input
          maxLength={100}
          value={source}
          placeholder={t('crm.leads.source_placeholder')}
          onChange={(event) => setSource(event.target.value)}
        />
      </label>
      {leads.length > 0 && !result && (
        <p className={styles.notice}>
          {t('crm.leads.import_ready', {
            count: leads.length,
            columns: columns
              .map((key) => t(`crm.leads.fields.${key}`))
              .join(', '),
          })}
        </p>
      )}
      {progress !== null && (
        <p role="status" className={styles.notice}>
          {t('crm.leads.import_progress', {
            done: progress,
            total: leads.length,
          })}
        </p>
      )}
      {result && (
        <p role="status" className={styles.successText}>
          {t('crm.leads.import_done', result)}
        </p>
      )}
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <div className={styles.actions}>
        <CrmButton
          variant="outline"
          type="button"
          onClick={() => dialogId && closeDialog(dialogId)}
        >
          {t(result ? 'crm.leads.close' : 'crm.cancel')}
        </CrmButton>
        {!result && (
          <CrmButton
            variant="adminPrimary"
            type="button"
            disabled={!leads.length || progress !== null}
            onClick={() => void run()}
          >
            {t('crm.leads.import_run', { count: leads.length })}
          </CrmButton>
        )}
      </div>
    </div>
  );
};
